import React, { useState, useEffect, useMemo } from 'react';
import { Button } from './Button';
import { toast } from 'sonner';
import packageManagerIcon from '/package-manager.png';
import styles from './PackageManagerModal.module.css';

interface PackageManagerModalProps {
  open: boolean;
  onClose: () => void;
  onInstall: (pkg: string) => void;
  favorites: string[];
  onToggleFavorite: (pkg: string) => void;
  queue: string[];
  onRemoveFromQueue: (pkg: string) => void;
  installedPackages?: Record<string, string>;
}

export const PackageManagerModal: React.FC<PackageManagerModalProps> = ({
  open,
  onClose,
  onInstall,
  favorites,
  onToggleFavorite,
  queue,
  onRemoveFromQueue,
  installedPackages = {},
}) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const installedEntries = useMemo(
    () => Object.entries(installedPackages).sort(([a], [b]) => a.localeCompare(b)),
    [installedPackages],
  );

  // Search npm packages when search term changes
  useEffect(() => {
    if (!search.trim()) {
      setResults([]);
      return;
    }

    const searchPackages = async () => {
      setLoading(true);
      try {
        // Use npm registry search API (proxied through our server to avoid CORS)
        const response = await fetch(`/_api/npm/search?text=${encodeURIComponent(search)}&size=20`);
        if (!response.ok) {
          throw new Error('Failed to search npm packages');
        }
        const data = await response.json();
        const packageNames =
          data.objects?.map((obj: any) => obj.package?.name).filter(Boolean) || [];
        setResults(packageNames);
      } catch (err) {
        console.error('Failed to search npm packages:', err);
        toast.error('Failed to search npm packages');
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search
    const timeoutId = setTimeout(() => {
      searchPackages();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            <img src={packageManagerIcon} alt="Package Manager" className={styles.icon} />
            <h2 className={styles.title}>Package Manager</h2>
          </div>
          <Button variant="outline" onClick={onClose} className={styles.closeButton}>
            Close
          </Button>
        </div>
        <div className={styles.body}>
          <input
            id="package-search"
            name="package-search"
            type="text"
            placeholder="Search packages (npm / pnpm / yarn)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
          {loading && <div className={styles.loading}>Loading...</div>}

          {installedEntries.length > 0 && (
            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>
                Installed Packages ({installedEntries.length})
              </h4>
              {installedEntries.map(([pkg, version]) => (
                <div key={pkg} className={styles.item}>
                  <span className={styles.pkgName}>
                    {pkg}
                    <span className={styles.version}>{version}</span>
                  </span>
                  <div className={styles.itemActions}>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onInstall(`${pkg}@latest`)}
                      disabled={queue.includes(pkg) || queue.includes(`${pkg}@latest`)}
                    >
                      {queue.includes(pkg) || queue.includes(`${pkg}@latest`) ? 'Queued' : 'Update'}
                    </Button>
                    <Button size="sm" variant="link" onClick={() => onToggleFavorite(pkg)}>
                      {favorites.includes(pkg) ? 'Unfavorite' : 'Favorite'}
                    </Button>
                  </div>
                </div>
              ))}
            </section>
          )}

          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>Favorites</h4>
            {favorites.length === 0 && <div className={styles.empty}>No favorites yet.</div>}
            {favorites.map((pkg) => (
              <div key={pkg} className={styles.item}>
                <span className={styles.pkgName}>{pkg}</span>
                <div className={styles.itemActions}>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onInstall(pkg)}
                    disabled={queue.includes(pkg)}
                  >
                    {queue.includes(pkg) ? 'Queued' : 'Install'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => !queue.includes(pkg) && onInstall(pkg)}
                    disabled={queue.includes(pkg)}
                  >
                    Add to Queue
                  </Button>
                  <Button size="sm" variant="link" onClick={() => onToggleFavorite(pkg)}>
                    Unfavorite
                  </Button>
                </div>
              </div>
            ))}
          </section>

          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>Search Results</h4>
            {results.length === 0 && !loading && <div className={styles.empty}>No results</div>}
            {results.map((pkg) => (
              <div key={pkg} className={styles.item}>
                <span className={styles.pkgName}>
                  {pkg}
                  {installedPackages[pkg] && (
                    <span className={styles.installedBadge}>installed</span>
                  )}
                </span>
                <div className={styles.itemActions}>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onInstall(installedPackages[pkg] ? `${pkg}@latest` : pkg)}
                    disabled={queue.includes(pkg) || queue.includes(`${pkg}@latest`)}
                  >
                    {queue.includes(pkg) || queue.includes(`${pkg}@latest`)
                      ? 'Queued'
                      : installedPackages[pkg]
                        ? 'Update'
                        : 'Install'}
                  </Button>
                  {!installedPackages[pkg] && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => !queue.includes(pkg) && onInstall(pkg)}
                      disabled={queue.includes(pkg)}
                    >
                      Add to Queue
                    </Button>
                  )}
                  <Button size="sm" variant="link" onClick={() => onToggleFavorite(pkg)}>
                    {favorites.includes(pkg) ? 'Unfavorite' : 'Favorite'}
                  </Button>
                </div>
              </div>
            ))}
          </section>

          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>Queue</h4>
            {queue.length === 0 && <div className={styles.empty}>No packages queued.</div>}
            {queue.map((pkg) => (
              <div key={pkg} className={styles.item}>
                <span className={styles.pkgName}>{pkg}</span>
                <div className={styles.itemActions}>
                  <Button size="sm" variant="ghost" onClick={() => onRemoveFromQueue(pkg)}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </section>
        </div>
      </div>
    </div>
  );
};
